import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type WithdrawEntitySuggestionResponse } from '@bliks-io/contracts';
import {
  invalidateSuggestionResolutionSurfaces,
  postEntitySuggestionVerb
} from './useEntitySuggestionAction';
import { entitySuggestionKeys } from '../query-keys';

/**
 * Guarded withdraw of an eagerly created suggestion, fired (fire-and-forget)
 * when a reassign flow is cancelled. The backend deletes only when the
 * suggestion is still pending AND unreferenced by any slot; every other case
 * is a successful no-op — so callers may withdraw optimistically without
 * checking first, and racing cancels never error.
 *
 * Unlike approve/merge/reject this isn't a `useEntitySuggestionAction`: the
 * id rides in the mutate variables (one instance serves a withdraw loop) and
 * the invalidation is conditional on the response — only the shared verb-POST
 * chokepoint is reused.
 */
export function useWithdrawEntitySuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suggestionId }: { suggestionId: string }) =>
      postEntitySuggestionVerb<undefined, WithdrawEntitySuggestionResponse>(
        suggestionId,
        'withdraw'
      ),
    onSuccess: (resp) => {
      // Only an actual deletion changes what any surface renders (ghost lists,
      // reviewer queue); the guarded no-op outcome leaves caches untouched.
      if (!resp.data.withdrawn) return;
      qc.invalidateQueries({ queryKey: entitySuggestionKeys.all });
      invalidateSuggestionResolutionSurfaces(qc, { entityLists: false });
    }
  });
}
